import { useEffect, useRef, useState } from 'react';
import { Button, Stack, Typography } from '@mui/material';
import { FormDialog, FormTextField, InlineNotice } from '../../../design-system';
import type { StaffingPlan } from '../operations/planModel';
import type { StaffingOperationsPort } from '../operations/port';
import { copyStaffingPlanInput } from '../model/staffingPlanPresentation';
import { saveFixtureStaffingPlan } from '../fixtures/staffingPlanFixtures';
import { useUnsavedChangesGuard } from '../../../shared/useUnsavedChangesGuard';

export function StaffingPlanRecoveryActions({ plan, port, fixtureMode, canCancel, disabled, onBusyChange, onChanged, onCopied }: {
  plan: StaffingPlan; port: StaffingOperationsPort; fixtureMode: boolean; canCancel: boolean; disabled?: boolean;
  onBusyChange: (busy: boolean) => void; onChanged: () => void; onCopied: (plan: StaffingPlan) => void;
}) {
  const [action, setAction] = useState<'cancel' | 'copy'>();
  const [comment, setComment] = useState('');
  const [date, setDate] = useState(plan.effectiveDate);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const pending = useRef(false);
  const attempt = useRef<{ payload: string; key: string } | undefined>(undefined);
  useEffect(() => { onBusyChange(busy || uncertain); return () => onBusyChange(false); }, [busy, uncertain, onBusyChange]);
  useUnsavedChangesGuard({ dirty: Boolean(action === 'cancel' && comment.trim() && !uncertain), busy: busy || uncertain });
  const run = async () => {
    if (!action || pending.current || (disabled && !uncertain) || (action === 'cancel' && !comment.trim()) || (action === 'copy' && !date)) return;
    const payload = JSON.stringify({ action, id: plan.id, comment: comment.trim(), date });
    if (attempt.current?.payload !== payload) attempt.current = { payload, key: crypto.randomUUID() };
    pending.current = true; setBusy(true); setError('');
    try {
      if (action === 'cancel') {
        if (!canCancel || plan.status !== 'SUBMITTED' || (!fixtureMode && (!plan.businessProcessId || !port.cancelSubmittedPlan))) return;
        if (fixtureMode) saveFixtureStaffingPlan({ ...plan, status: 'CANCELED' });
        else await port.cancelSubmittedPlan!(plan.businessProcessId!, comment.trim(), attempt.current.key);
        onChanged();
      } else {
        if (!['DENIED', 'CANCELED'].includes(plan.status)) return;
        const input = { ...copyStaffingPlanInput(plan), effectiveDate: date };
        const copied: StaffingPlan = fixtureMode ? { ...input, id: crypto.randomUUID(), version: 1, status: 'DRAFT', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
          : await port.createPlan(input, attempt.current.key);
        if (fixtureMode) saveFixtureStaffingPlan(copied);
        onCopied(copied);
      }
      setUncertain(false); setAction(undefined); attempt.current = undefined;
    } catch (reason) {
      const status = reason && typeof reason === 'object' && 'status' in reason ? Number(reason.status) : undefined;
      setUncertain(!status || status >= 500);
      setError(reason instanceof Error ? reason.message : '처리 결과를 확인하지 못했습니다. 같은 작업을 다시 시도해 주세요.');
    }
    finally { pending.current = false; setBusy(false); }
  };
  return <>
    {plan.status === 'SUBMITTED' && canCancel && (fixtureMode || (plan.businessProcessId && port.cancelSubmittedPlan)) ? <Button variant="outlined" color="error" disabled={disabled || busy} onClick={() => { setComment(''); setError(''); setAction('cancel'); }}>요청 취소</Button> : null}
    {['DENIED', 'CANCELED'].includes(plan.status) ? <Button variant="outlined" disabled={disabled || busy} onClick={() => { setDate(plan.effectiveDate); setError(''); setAction('copy'); }}>복사해서 수정</Button> : null}
    <FormDialog open={Boolean(action)} title={action === 'cancel' ? '발령 요청을 취소할까요?' : '새 발령안으로 복사'} onClose={() => { if (!busy && !uncertain) setAction(undefined); }} onSubmit={run} busy={busy}
      submitLabel={uncertain ? '같은 요청 다시 시도' : action === 'cancel' ? '요청 취소' : '복사해서 수정'} cancelLabel="돌아가기" submitColor={action === 'cancel' ? 'error' : 'primary'} submitDisabled={action === 'cancel' ? !comment.trim() : !date}>
      <Stack spacing={2}>
        <Typography variant="body2">{plan.title}</Typography>
        <Typography variant="body2" color="text.secondary">{action === 'cancel' ? '결재 진행을 중단하고 사유를 이력에 남깁니다. 취소 후 변경 내용을 복사해 수정하고 다시 승인 요청할 수 있습니다.' : '변경 내용과 사유를 새 초안으로 복사합니다. 원 요청의 승인 이력은 유지되며 수정 후 다시 승인을 받아야 합니다.'}</Typography>
        {action === 'cancel' ? <FormTextField label="취소 사유" required value={comment} onChange={event => setComment(event.target.value)} multiline minRows={2} disabled={busy || uncertain} slotProps={{ htmlInput: { maxLength: 2000 } }} />
          : <FormTextField label="시행일" type="date" required value={date} onChange={event => setDate(event.target.value)} disabled={busy || uncertain} />}
        {uncertain ? <InlineNotice severity="warning">처리 결과를 확인할 때까지 입력과 닫기가 잠깁니다. 같은 요청으로 다시 시도하면 중복으로 처리되지 않습니다.</InlineNotice> : null}
        {error ? <InlineNotice severity="error">{error}</InlineNotice> : null}
      </Stack>
    </FormDialog>
  </>;
}
